import { useEffect, useMemo, useRef, useState } from 'react';
import {
  formatEuro,
  formatPercent,
  formatSquareMeters,
  groupDocumentTypes,
  type AcquisitionDocumentFigure,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Eyebrow, Popover, SearchField, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { assignDocument, useDocumentTypes } from '../api';
import type { CalculationVersion, DocumentType, WizardDocument, WizardUploadRow } from '../types';
import { CardIntro } from './WizardShell';
import styles from './wizard.module.css';

const SECONDS_PER_DOCUMENT = 10;

function isManual(row: WizardUploadRow): boolean {
  return (
    row.state !== 'error' &&
    (row.classification.state === 'suggested' || row.classification.state === 'unassigned')
  );
}

function etaText(pending: number): string {
  const minutes = Math.max(1, Math.ceil((pending * SECONDS_PER_DOCUMENT) / 60));
  return minutes === 1 ? 'noch etwa 1 Minute' : `noch etwa ${minutes} Minuten`;
}

function statusClass(row: WizardUploadRow): string | undefined {
  switch (row.classification.state) {
    case 'classified':
    case 'confirmed':
      return styles.docStatusDone;
    case 'pending':
      return row.state === 'queued' ? undefined : styles.docStatusReading;
    case 'suggested':
    case 'unassigned':
      return styles.docStatusWarn;
  }
}

function statusLabel(row: WizardUploadRow): string {
  if (row.state === 'error') return row.stateLabel;
  if (row.classification.state === 'pending' && row.state === 'queued') return 'wartet';
  return row.classification.label;
}

// --- document type picker (Objekt anlegen 04 popover) -------------------------------------------------

type TypeChoice = Pick<DocumentType, 'id' | 'name'>;

interface TypePickerProps {
  row: WizardUploadRow;
  types: DocumentType[];
  selected: TypeChoice | null;
  onSelect: (type: DocumentType) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function TypePicker({ row, types, selected, onSelect, open, onOpenChange }: TypePickerProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const suggestion = row.classification.documentType;
  const suggestions = useMemo(() => {
    const list: DocumentType[] = [];
    const found = suggestion ? types.find((type) => type.id === suggestion.id) : undefined;
    if (found) {
      list.push(found);
      for (const type of types) {
        if (type.id !== found.id && type.group === found.group && list.length < 3) list.push(type);
      }
    }
    return list;
  }, [suggestion, types]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return types;
    return types.filter((type) => type.name.toLowerCase().includes(q));
  }, [query, types]);
  const grouped = useMemo(() => groupDocumentTypes(filtered, null), [filtered]);
  const searching = query.trim() !== '';
  const listAll = showAll || searching || suggestions.length === 0;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setShowAll(false);
    }
  }, [open]);

  const pick = (type: DocumentType) => {
    onSelect(type);
    onOpenChange(false);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={clsx(styles.assignSelect, !selected && styles.assignSelectEmpty)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Dokumentart für ${row.name}`}
        onClick={() => onOpenChange(!open)}
      >
        <span className={styles.assignSelectText}>{selected ? selected.name : 'Dokumentart wählen'}</span>
        <span className={styles.assignCaret} aria-hidden="true">
          ▾
        </span>
      </button>
      <Popover
        open={open}
        onClose={() => onOpenChange(false)}
        anchorRef={anchorRef}
        placement="bottom-start"
        width={250}
        variant="menu"
        initialFocus="first"
        label={`Dokumentart für ${row.name}`}
      >
        <div className={styles.typeSearch}>
          <SearchField
            label="Dokumentart suchen"
            placeholder="Dokumentart suchen"
            tone="sunken"
            size="compact"
            icon={false}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        {!listAll ? (
          <>
            <div className={styles.typeGroup}>Vorschläge</div>
            {suggestions.map((type) => (
              <button
                key={type.id}
                type="button"
                className={clsx(styles.typeOption, selected?.id === type.id && styles.typeOptionActive)}
                onClick={() => pick(type)}
              >
                {type.name}
              </button>
            ))}
            <div className={styles.typeSeparator} aria-hidden="true" />
            <button type="button" className={styles.typeOption} onClick={() => setShowAll(true)}>
              Alle {types.length} Dokumentarten
            </button>
          </>
        ) : (
          <div className={styles.typeList}>
            {filtered.length === 0 ? (
              <div className={clsx(styles.typeOption, styles.assignSelectEmpty)}>
                Keine Dokumentart gefunden.
              </div>
            ) : (
              grouped.map((group) => (
                <div key={group.group} role="group" aria-label={group.label}>
                  <div className={styles.typeGroup}>{group.label}</div>
                  {group.items.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      className={clsx(styles.typeOption, selected?.id === type.id && styles.typeOptionActive)}
                      onClick={() => pick(type)}
                    >
                      {type.name}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        )}
      </Popover>
    </>
  );
}

// --- main column -------------------------------------------------------------------------------------

export interface ClassificationMainProps {
  doc: WizardDocument;
  selectedId: string | null;
  onSelect: (documentId: string) => void;
  onChanged: () => void;
}

/** Objekt anlegen 03/04: the reading table and the manual assignment rows. */
export function ClassificationMain({ doc, selectedId, onSelect, onChanged }: ClassificationMainProps) {
  const { toast } = useToast();
  const types = useDocumentTypes();
  const [choices, setChoices] = useState<Record<string, TypeChoice | null>>({});
  const [openPicker, setOpenPicker] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const rows = doc.uploads.filter((row) => row.state !== 'error');
  const manual = rows.filter(isManual);
  const read = rows.filter((row) => !isManual(row));
  const { classification } = doc.steps;
  const recognised = classification.total - classification.uncertain;
  // Property documents: the types whose scope contains the property (0032), grouped in the picker.
  const typeList = (types.data?.items ?? []).filter((type) => type.scope.includes('property'));

  const choiceOf = (row: WizardUploadRow): TypeChoice | null => {
    const chosen = choices[row.documentId];
    if (chosen !== undefined) return chosen;
    const suggestion = row.classification.documentType;
    return suggestion ? { id: suggestion.id, name: suggestion.name } : null;
  };

  async function assign(row: WizardUploadRow, typeId: string | null) {
    setBusy(row.documentId);
    try {
      await assignDocument(row.documentId, typeId);
      toast({
        message: typeId === null ? `${row.name} bleibt unter „Sonstiges“.` : `${row.name} zugeordnet.`,
      });
      onChanged();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      {classification.notice && manual.length > 0 ? (
        <div className={styles.warningBar} role="status">
          <span className={styles.warningTitle}>{classification.notice}</span>
          {recognised > 0 ? (
            <span className={styles.warningText}>
              {recognised === 1
                ? 'Das übrige 1 ist erkannt und übernommen.'
                : `Die übrigen ${recognised} sind erkannt und übernommen.`}
            </span>
          ) : null}
        </div>
      ) : null}

      {manual.length > 0 ? (
        <section className={styles.card} aria-labelledby="wizard-review">
          <CardIntro
            title="Bitte kurz prüfen"
            text="Prüfen Sie den Vorschlag oder wählen Sie die passende Dokumentart. Nicht zugeordnete Dateien bleiben unter „Sonstiges“ in der Objektakte."
          />
          <div className={styles.assignRows}>
            {manual.map((row) => {
              const selected = choiceOf(row);
              const active = row.documentId === selectedId;
              return (
                <div
                  key={row.documentId}
                  className={clsx(styles.assignRow, active && styles.assignRowActive)}
                  onFocusCapture={() => onSelect(row.documentId)}
                  onClick={() => onSelect(row.documentId)}
                >
                  <span className={styles.fileText}>
                    <span className={styles.assignName}>{row.name}</span>
                    <span className={styles.assignLabel}>{row.classification.label}</span>
                  </span>
                  <TypePicker
                    row={row}
                    types={typeList}
                    selected={selected}
                    onSelect={(type) => setChoices((current) => ({ ...current, [row.documentId]: type }))}
                    open={openPicker === row.documentId}
                    onOpenChange={(open) => setOpenPicker(open ? row.documentId : null)}
                  />
                  <span className={styles.assignActions}>
                    <button
                      type="button"
                      className={clsx('pressable', styles.smallButton)}
                      disabled={selected === null || busy !== null}
                      aria-busy={busy === row.documentId || undefined}
                      onClick={() => void assign(row, selected?.id ?? null)}
                    >
                      Übernehmen
                    </button>
                    <button
                      type="button"
                      className={clsx('pressable', styles.smallButton)}
                      disabled={busy !== null}
                      onClick={() => void assign(row, null)}
                    >
                      Kein Objektdokument
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {read.length > 0 || manual.length === 0 ? (
        <section className={styles.card} aria-labelledby="wizard-reading">
          <div className={styles.cardHead}>
            <div className={styles.cardHeadText}>
              <h2 id="wizard-reading" className={styles.cardTitle}>
                {classification.pending > 0 ? 'Unterlagen werden gelesen' : 'Unterlagen gelesen'}
              </h2>
              <p className={styles.cardText}>
                DAMOKLES erkennt die Dokumentart und übernimmt Kennzahlen in Kalkulation und Checkliste. Sie
                können währenddessen weiterarbeiten.
              </p>
            </div>
            <div className={styles.counter}>
              <div className={styles.counterValue}>
                {classification.classified} / {classification.total}
              </div>
              {classification.pending > 0 ? (
                <div className={styles.counterText}>{etaText(classification.pending)}</div>
              ) : null}
            </div>
          </div>
          <div
            className={styles.readBar}
            role="progressbar"
            aria-label="Gelesene Unterlagen"
            aria-valuemin={0}
            aria-valuemax={classification.total}
            aria-valuenow={classification.classified}
          >
            <span
              className={styles.readBarFill}
              style={{
                width: `${classification.total === 0 ? 0 : Math.round((classification.classified / classification.total) * 100)}%`,
              }}
            />
          </div>
          <div className={styles.docTable}>
            <div className={styles.docTableHead} aria-hidden="true">
              <span>Datei</span>
              <span>Erkannt als</span>
              <span>Sicherheit</span>
              <span>Status</span>
            </div>
            <div className={styles.docTableBody}>
              {read.length === 0 ? (
                <div className={styles.docTableRow}>
                  <span className={clsx(styles.docType, styles.docTypeEmpty)}>
                    Noch keine Unterlagen hochgeladen.
                  </span>
                </div>
              ) : (
                read.map((row) => (
                  <div key={row.documentId} className={styles.docTableRow}>
                    <span className={styles.docName}>{row.name}</span>
                    <span
                      className={clsx(
                        styles.docType,
                        !row.classification.documentType && styles.docTypeEmpty,
                      )}
                    >
                      {row.classification.documentType?.name ?? '–'}
                    </span>
                    <span className={styles.docConfidence}>
                      {row.classification.confidence === null || row.classification.state === 'confirmed'
                        ? '–'
                        : `${Math.round(row.classification.confidence)} %`}
                    </span>
                    <span className={clsx(styles.docStatus, statusClass(row))}>
                      <span className={styles.docDot} aria-hidden="true" />
                      {statusLabel(row)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}

// --- sidebar -----------------------------------------------------------------------------------------

const RECOGNISED_LABELS: Record<AcquisitionDocumentFigure, string> = {
  livingAreaTotalSqm: 'Wohnfläche gesamt',
  actualRentPaCents: 'Ist-Miete p. a.',
  maintenanceReserveCents: 'Erhaltungsrücklage',
  purchasePriceCents: 'Kaufpreis gesamt',
  brokerCommissionPct: 'Käufer-Maklercourtage',
};

/** The figures read from documents in the order of Objekt anlegen 03 (area, rent, reserve, price, commission). */
const RECOGNISED_ORDER: AcquisitionDocumentFigure[] = [
  'livingAreaTotalSqm',
  'actualRentPaCents',
  'maintenanceReserveCents',
  'purchasePriceCents',
  'brokerCommissionPct',
];

function recognisedRows(current: CalculationVersion | null) {
  if (!current) return [];
  return RECOGNISED_ORDER.flatMap((key) => {
    const figure = current.documentFigures.find((entry) => entry.key === key);
    if (!figure) return [];
    const value =
      key === 'livingAreaTotalSqm'
        ? formatSquareMeters(figure.value)
        : key === 'brokerCommissionPct'
          ? formatPercent(figure.value, 2)
          : formatEuro(figure.value);
    return [{ field: key, label: RECOGNISED_LABELS[key], value, source: figure.source }];
  });
}

export interface ClassificationSidebarProps {
  doc: WizardDocument;
  selectedId: string | null;
}

/** Objekt anlegen 04 „Vorschau · <Datei>" for the selected manual row, otherwise 03 „Bisher erkannt". */
export function ClassificationSidebar({ doc, selectedId }: ClassificationSidebarProps) {
  const manual = doc.uploads.filter(isManual);
  const selected = manual.find((row) => row.documentId === selectedId) ?? manual[0] ?? null;
  if (selected) {
    const isImage = selected.mime?.startsWith('image/') && selected.mime !== 'image/vnd.dwg';
    const isPdf = selected.mime === 'application/pdf';
    const src = selected.fileId && selected.state === 'uploaded' ? `/api/files/${selected.fileId}` : null;
    return (
      <section className={styles.card} aria-labelledby="wizard-preview">
        <Eyebrow id="wizard-preview">Vorschau · {selected.name}</Eyebrow>
        <div className={styles.preview}>
          {src && isImage ? (
            <img src={src} alt={`Vorschau ${selected.name}`} className={styles.previewImage} />
          ) : src && isPdf ? (
            <iframe
              src={`${src}#toolbar=0&view=FitH`}
              title={`Vorschau ${selected.name}`}
              className={styles.previewFrame}
            />
          ) : (
            <div className={styles.previewEmpty}>
              {selected.state === 'queued'
                ? 'Die Datei wird noch geprüft.'
                : 'Für diese Datei gibt es keine Vorschau.'}
            </div>
          )}
        </div>
        <div className={styles.keywordsLabel}>Erkannte Stichworte</div>
        <div className={styles.keywords}>
          {selected.classification.keywords.length === 0 ? (
            <span className={styles.counterText}>Keine Stichworte erkannt.</span>
          ) : (
            selected.classification.keywords.map((keyword) => (
              <span key={keyword} className={styles.keyword}>
                {keyword}
              </span>
            ))
          )}
        </div>
      </section>
    );
  }
  const rows = recognisedRows(doc.calculations.current);
  return (
    <section className={styles.card} aria-labelledby="wizard-recognised">
      <Eyebrow id="wizard-recognised">Bisher erkannt</Eyebrow>
      <div className={styles.versions}>
        {rows.length === 0 ? (
          <div className={styles.recognisedRow}>
            <div className={styles.recognisedSource}>
              Noch keine Kennzahlen aus den Unterlagen übernommen.
            </div>
          </div>
        ) : (
          rows.map((row) => (
            <div key={row.field} className={styles.recognisedRow}>
              <div className={styles.recognisedLine}>
                <span className={styles.recognisedLabel}>{row.label}</span>
                <span className={styles.recognisedValue}>{row.value}</span>
              </div>
              <div className={styles.recognisedSource}>{row.source}</div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
